import React from "react";
import { CheckCircle2, Pencil } from "lucide-react";
import formatValue from "../../../utils/formatValue";
import { IFinancingInsight } from "../types/ownerDashboard.types";
import { formatAmortizationSystemLabel } from "../utils/formatAmortizationSystem";
import { formatMilestoneDate } from "../utils/formatMilestoneDate";
import { formatProjectionBasis } from "../utils/formatProjectionBasis";
import { formatPropertyReturnDate } from "../utils/formatPropertyReturnDate";
import { getFinancingProgressPct } from "../utils/getFinancingProgressPct";
import FinancingCoverageBar from "./FinancingCoverageBar";
import FinancingMilestoneChart from "./FinancingMilestoneChart";

interface FinancingInsightDetailProps {
  insight: IFinancingInsight;
  propertyLabel: string;
  ipca12mAccumulatedPct: number;
  onEdit: () => void;
}

export default function FinancingInsightDetail({
  insight,
  propertyLabel,
  ipca12mAccumulatedPct,
  onEdit,
}: FinancingInsightDetailProps) {
  const progressPct = getFinancingProgressPct(insight);

  return (
    <article className="flex min-w-0 flex-col gap-4 rounded-2xl border border-[#6ea8ff]/20 bg-[#1c2128] p-3 sm:p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="line-clamp-2 text-xs font-semibold text-[#8b949e]">
            {propertyLabel}
          </p>
          <p className="mt-1 text-xs text-[#6e7681] sm:text-sm">
            {formatAmortizationSystemLabel(insight.amortization_system)}
            {insight.payment_start_date
              ? ` · Financiamento desde ${formatPropertyReturnDate(insight.payment_start_date)}`
              : ""}
          </p>
          <p className="mt-1 text-xs text-[#6e7681] sm:text-sm">
            {formatProjectionBasis(insight.projection_basis)}
          </p>
        </div>

        <button
          type="button"
          onClick={onEdit}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/[0.08] px-3 py-1.5 text-xs font-semibold text-[#8b949e] transition-colors hover:border-[#6ea8ff]/35 hover:bg-[#6ea8ff]/10 hover:text-[#6ea8ff]"
        >
          <Pencil className="h-3.5 w-3.5" />
          Editar
        </button>
      </div>

      <FinancingCoverageBar
        coveragePct={progressPct}
        coversInstallment={insight.covers_installment}
      />

      {insight.covers_installment && (
        <div className="flex items-start gap-2 rounded-xl border border-[#3fb950]/30 bg-[#3fb950]/10 p-3">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#3fb950]" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#3fb950]">
              Cobertura integral da parcela ({progressPct.toFixed(0)}%)
            </p>
            <p className="mt-1 text-xs text-[#8b949e]">
              Média líquida: {formatValue(insight.average_monthly_net_rent)} · Parcela:{" "}
              {formatValue(insight.monthly_installment)}
            </p>
          </div>
        </div>
      )}

      <div className="min-w-0 overflow-hidden">
        <FinancingMilestoneChart
          insight={insight}
          ipca12mAccumulatedPct={ipca12mAccumulatedPct}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="min-w-0">
          <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
            Aluguel líquido médio
          </p>
          <p className="mt-1 truncate font-bold text-[#f0f6fc]">
            {formatValue(insight.average_monthly_net_rent)}
          </p>
        </div>
        <div className="min-w-0">
          <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
            Parcela
          </p>
          <p className="mt-1 truncate font-bold text-[#f0f6fc]">
            {formatValue(insight.monthly_installment)}
          </p>
        </div>
        {!insight.covers_installment && (
          <>
            <div className="min-w-0">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
                Diferença mensal
              </p>
              <p className="mt-1 truncate font-bold text-[#f0883e]">
                {formatValue(insight.monthly_gap)}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
                Marco estimado
              </p>
              <p className="mt-1 truncate font-bold text-[#6ea8ff]">
                {formatMilestoneDate(insight.estimated_milestone_date)}
              </p>
            </div>
          </>
        )}
      </div>

      {!insight.covers_installment &&
        insight.months_until_milestone !== null &&
        insight.months_until_milestone > 0 && (
          <p className="text-xs text-[#6e7681]">
            Previsão em aproximadamente {insight.months_until_milestone} meses
          </p>
        )}
    </article>
  );
}
